/* eslint-disable no-plusplus */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';

import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import _isEqual from 'lodash/isEqual';
import _isEmpty from 'lodash/isEmpty';
import _intersection from 'lodash/intersection';
import _difference from 'lodash/difference';
import _unique from 'lodash/uniq';
import { IconButton, Icon } from '@picsio/ui';
import {
  CheckIcon,
  CloseIcon,
  SearchIcon,
  Close,
} from '@picsio/icons';
import { usePrevious } from 'react-use';
import { MIME_TYPES } from '@picsio/db/src/constants';

import Logger from '../../../services/Logger';
import picsioConfig from '../../../../../../config';
import {
  BOOL_OPTIONS, SINGLE_OPERATORS, FILTER_NAMES_ASSETS_PRODUCTS, OPERATORS_ORDER, MULTIPLE_OPERATORS,
} from '../config';
import showAddNewFiltersDialog from '../../../helpers/showSearchFiltersDialog';
import showSaveSearchDialog from '../../SaveSearchDialog';
import l10n from '../../../shared/strings';
import Tooltip from '../../Tooltip';
import Autocomplete from '../../Search/Autocomplete';
import * as utils from '../../../shared/utils';
import MediaSearchFilters from './MediaSearchFilters';
import {
  getSearchProps, isRouteSearch, reloadRoute, setSearchRoute,
} from '../../../helpers/history';
import ua from '../../../ua';
import { changeTree, setMobileAdditionalScreenPanel, setMobileMainScreenPanel } from '../../../store/actions/main';
import { getByIds } from '../../../store/keywordsList/actions';
import { setSortType as setInboxSortType } from '../../../store/inboxes/actions';
import { activeCollectionSelector } from '../../../store/selectors/collections';
import updateUser from '../../../store/actions/user/updateUser';
import * as UtilsCollections from '../../../store/utils/collections';
import checkForCollectionsSortingsEquality from '../../../helpers/checkForCollectionsSortingsEquality';
import { activeSortTypeSelector } from '../../../store/inboxes/selectors';
import { updateSortType } from '../../../store/actions/lightboards';
import { getAssets } from '../../../store/actions/assets';
import Counter from '../../Search/Counter';
import TrialDemoControl from '../../toolbars/TrialDemoControl';
import prepareDataForAppliedFilterEvent from '../helpers/prepareDataForAppliedFilterEvent';
import {
  ensurePinnedSearchFiltersOrder,
  getPinnedSearchFilterIds,
  isPinnedSearchFilter,
  sortPinnedFiltersFirst,
} from '../helpers/pinnedSearchFilters';
import {
  normalizeProductLayerOptions,
  restoreProductLayerFilterValue,
  serializeProductLayerForRoute,
} from '../helpers/productLayerFilter';

import './styles.scss';

const defaultSort = { type: 'uploadTime', order: 'desc' };
const booleanSearchOperators = [' OR ', ' AND ', ' NOT '];

const SearchFiltersToriut = ({ onlyTextSearch = false }) => {
  const dispatch = useDispatch();
  const assets = useSelector((state) => state.assets);
  const products = useSelector((state) => state.products);
  const user = useSelector((state) => state.user);
  const teamSalesChannels = useSelector((state) => state.user.team?.salesChannels || []);
  const teammates = useSelector((state) => state.teammates);
  const location = useSelector((state) => state.router.location);
  const openedTree = useSelector((state) => state.main.openedTree);
  const rootCollection = useSelector((state) => state.collections.collections?.my);
  const { activeLightboard } = useSelector((state) => state.lightboards);
  const FILTERS = useMemo(() => FILTER_NAMES_ASSETS_PRODUCTS, []);

  const keywordsList = useSelector((state) => state.keywordsList.items);
  const treeKeywords = useSelector((state) => state.keywords.all);
  const allFaces = useSelector((state) => state.faces.all);
  const activeCollections = useSelector(activeCollectionSelector);
  const collectionsSortType = useSelector((state) => state.collections.activeCollections.map((activeCollection) => activeCollection?.sortType));
  const lightboardsSortType = useSelector((state) => state.lightboards.activeLightboard?.sortType);
  const { inboxes, activeInboxID } = useSelector((state) => state.inboxes);
  const customFields = useSelector((state) => state.customFields.items);
  const activeInboxSortType = useSelector(activeSortTypeSelector);
  const prevLocation = usePrevious(location);
  const prevCollectionsSortType = usePrevious(collectionsSortType[0]);
  const prevCollectionsSortTypeLength = usePrevious(collectionsSortType.length);
  const prevLightboardsSortType = usePrevious(lightboardsSortType);
  const prevInboxesSortType = usePrevious(activeInboxSortType);
  const watermarks = useSelector((state) => state.assets.watermarks);
  const [sortType, setSortType] = useState(defaultSort);
  const [isCollapsed, setIsCollapsed] = useState(utils.LocalStorage.get('picsio.searchFiltersMinimized'));
  const [hiddenItemsCount, setHiddenItemsCount] = useState(0);
  const [items, setItems] = useState([]);
  const [itemWidths, setItemWidths] = useState({});
  const [isHovering, setIsHovering] = useState(false);
  const timeout = useRef();
  const [searchInputTarget, setSearchInputTarget] = useState(null);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      setSearchInputTarget(document.getElementById('search-input'));
    }
  }, []);
  const availableSearchFilters = useMemo(() => (user.availableSearchFilters?.assets || {}), [user.availableSearchFilters?.assets]);
  const searchFiltersOrder = useMemo(() => [...(user?.settings?.favoriteSearchFilters?.assets ?? [])], [user?.settings?.favoriteSearchFilters?.assets]);
  const pinnedFilterIds = useMemo(
    () => getPinnedSearchFilterIds(availableSearchFilters),
    [availableSearchFilters],
  );
  const [customizingMode, setCustomizingMode] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [confirmedFilters, setConfirmedFilters] = useState({});

  const [searchValue, setSearchValue] = useState('');

  const [open, setOpen] = useState('');

  const normalizeFieldOptions = useCallback((id, options) => {
    if (id === 'productLayer') {
      return normalizeProductLayerOptions(options, teamSalesChannels);
    }
    if (FILTERS[id]?.options) {
      return options.map((option) => ({ label: FILTERS[id].options[option], value: option }));
    }
    return options.map((option) => ({ label: option, value: option }));
  }, [FILTERS, teamSalesChannels]);

  const generateOperatorsOrder = useCallback((operators, field, id) => {
    if (OPERATORS_ORDER[id]) {
      const common = _intersection([...OPERATORS_ORDER[id]], [...operators]);
      const rest = _difference([...operators], [...OPERATORS_ORDER[id]]);
      return [...common, ...rest];
    }
    if (field.type === 'date') {
      const common = _intersection([...OPERATORS_ORDER.date], [...operators]);
      const rest = _difference([...operators], [...OPERATORS_ORDER.date]);
      return [...common, ...rest];
    }
    if (field.type === 'number') {
      const common = _intersection([...OPERATORS_ORDER.number], [...operators]);
      const rest = _difference([...operators], [...OPERATORS_ORDER.number]);
      return [...common, ...rest];
    }
    if (field.type === 'enum' && field.multiple) {
      const common = _intersection([...OPERATORS_ORDER.enumMultiple], [...operators]);
      const rest = _difference([...operators], [...OPERATORS_ORDER.enumMultiple]);
      return [...common, ...rest];
    }
    if (field.type === 'enum' && !field.multiple) {
      const common = _intersection([...OPERATORS_ORDER.enumSingle], [...operators]);
      const rest = _difference([...operators], [...OPERATORS_ORDER.enumSingle]);
      return [...common, ...rest];
    }
    if (id === 'comment' && field.type === 'string') {
      const common = _intersection([...OPERATORS_ORDER.comment], [...operators]);
      const rest = _difference([...operators], [...OPERATORS_ORDER.comment]);
      return [...common, ...rest];
    }
    if (field.type === 'string' || field.type === 'text') {
      const common = _intersection([...OPERATORS_ORDER.text], [...operators]);
      const rest = _difference([...operators], [...OPERATORS_ORDER.text]);
      return [...common, ...rest];
    }
    return operators;
  }, []);

  const normalizeDefaultSearchFields = useCallback((fields, type) => fields.filter(([, { hidden }]) => !hidden).map(([id, field]) => {
    const fieldData = {
      ...field,
      operators: generateOperatorsOrder(field.operators, field, id),
      id,
      title: FILTERS[id]?.title || id,
    };
    if (field.options) {
      fieldData.options = normalizeFieldOptions(id, field.options);
    }

    if (field.type === 'boolean') {
      fieldData.options = BOOL_OPTIONS;
    }

    if (field.type === 'productLayer') {
      fieldData.operators = ['oneOf'];
    }

    if (FILTERS[id]?.booleanOptions) {
      fieldData.options = FILTERS[id].booleanOptions;
    }
    if (FILTERS[id]?.booleanOptions) {
      fieldData.filterType = type;
    }
    return fieldData;
  }), [FILTERS, normalizeFieldOptions, generateOperatorsOrder]);

  const initFilters = useMemo(
    () => normalizeDefaultSearchFields(Object.entries(availableSearchFilters)),
    [availableSearchFilters, normalizeDefaultSearchFields],
  );
  const [activeFilters, setActiveFilters] = useState(initFilters);
  const [order, setOrder] = useState(() => ensurePinnedSearchFiltersOrder(
    searchFiltersOrder?.length ? searchFiltersOrder : initFilters.map(({ id }) => id),
    pinnedFilterIds,
  ));
  const [prevFilters, setPrevFilters] = useState(activeFilters);
  const inactiveFilters = useMemo(
    () => activeFilters.filter(({ id }) => !order?.find((item) => item === id)
      && !isPinnedSearchFilter(id, pinnedFilterIds)),
    [order, activeFilters, pinnedFilterIds],
  );

  const [prevOrder, setPrevOrder] = useState(order);

  const filterIsHidden = useCallback((entity) => {
    if (entity === 'lightboardId' || entity === 'inboxId') return true;
    if (entity === 'collectionIds' && activeCollections.length === 1 && activeCollections[0]?._id === rootCollection?._id) return true;
    return false;
  }, [activeCollections, rootCollection?._id]);

  const resetIsHidden = useMemo(() => {
    const filters = { ...confirmedFilters };
    delete filters.text;
    delete filters.searchSource;

    if (!Object.keys(filters).length) return true;

    if (Object.keys(filters).length === 1) {
      if (filters.collectionIds && filters.collectionIds?.value?.length === 1 && filters.collectionIds.value[0]._id === rootCollection?._id) return true;
      if (filters.lightboardId) return true;
      if (filters.inboxId) return true;
      return false;
    }
    return false;
  }, [rootCollection?._id, confirmedFilters]);

  const renderItems = useMemo(() => {
    const data = order.map((item) => activeFilters.find((filter) => filter.id === item));
    return data.filter(Boolean);
  }, [activeFilters, order]);

  const renderItemsMedia = useMemo(() => renderItems.filter((item) => !item.product), [renderItems]);
  const renderItemsProducts = useMemo(
    () => sortPinnedFiltersFirst(renderItems.filter((item) => item.product), pinnedFilterIds),
    [renderItems],
  );
  const itemsMediaRef = useRef([]);
  itemsMediaRef.current = renderItemsMedia.map((_, i) => itemsMediaRef.current[i] || React.createRef());
  const itemsProductsRef = useRef([]);
  itemsProductsRef.current = renderItemsProducts.map((_, i) => itemsProductsRef.current[i] || React.createRef());

  const containerRef = useRef(null);
  const controlsRef = useRef(null);
  const hiddenItemsCounterRef = useRef(null);
  const internalSubmit = useRef(false);
  const searchSource = useRef('searchbar');
  const searchTagIds = useRef(null);
  const searchInboxId = useRef(null);
  const searchLightboardId = useRef(null);

  const originalEl = useRef();
  const draggableElement = useRef();
  const tmpItem = useRef();
  const tmpPosition = useRef(null);
  const itemToMove = useRef('');
  const itemToMoveAfter = useRef('');

  const templateTmpItem = useRef(document.createElement('div'));
  templateTmpItem.current.classList.add('temporarySearchFilterItem');

  const onRouteSearch = () => {
    const searchProps = getSearchProps();
    searchTagIds.current = searchProps.collectionIds;
    searchLightboardId.current = searchProps.lightboardId;
    searchInboxId.current = searchProps.inboxId;
    let searchOrder = searchFiltersOrder;

    const nonExistingFilters = Object.keys(searchProps).filter((entity) => !(searchFiltersOrder || [])?.includes(entity) && !filterIsHidden(entity));
    if (nonExistingFilters.length > 0) {
      searchOrder = [...order, ...nonExistingFilters];
    }

    if (internalSubmit.current) {
      internalSubmit.current = false;
      return;
    }

    const result = { text: '' };
    Object.entries(searchProps).forEach(([key, value]) => {
      if (key.endsWith('Operator')) {
        const id = key.replace(/Operator/ig, '');
        if (result[id]) {
          result[id].operator = value;
        } else {
          result[id] = { operator: value };
        }
      } else if (key === 'params2') {
        result.params2 = value;
      } else if (result[key]) result[key].value = value;
      else result[key] = { value };
    });

    const metaFields = Object.keys(searchProps).filter((searchFilter) => searchFilter.startsWith('meta.') && !searchFilter.includes('Operator'));
    if (metaFields) {
      customFields.forEach((customField) => {
        const customFieldTitle = `meta.${customField.title}`;
        const isMultipleOperator = MULTIPLE_OPERATORS.includes(result[customFieldTitle]?.operator);
        if (metaFields.includes(customFieldTitle) && customField.type === 'enum' && searchProps[customFieldTitle]) {
          if (Array.isArray(searchProps[customFieldTitle])) {
            result[customFieldTitle].value = searchProps[customFieldTitle].map((option) => ({ title: option, value: option, _id: option }));
          } else {
            result[customFieldTitle].value = [{ title: searchProps[customFieldTitle], value: searchProps[customFieldTitle], _id: searchProps[customFieldTitle] }];
          }
        }

        if (isMultipleOperator && searchProps.params2[customFieldTitle]) {
          searchProps.params2[customFieldTitle].forEach((option) => {
            const item = { title: option, value: option, _id: option };
            if (result[customFieldTitle]?.excludedValues) {
              result[customFieldTitle].excludedValues.push(item);
            } else {
              result[customFieldTitle].excludedValues = [item];
            }
          });
        }
      });
    }

    const confirmedUsers = teammates.items.filter((teammate) => teammate.parent === undefined || teammate.parent.confirmed === true);
    if (searchProps?.commentedBy || searchProps.params2?.commentedBy) {
      const isMultipleOperator = MULTIPLE_OPERATORS.includes(result?.commentedBy?.operator);
      if (searchProps?.commentedBy) {
        if (Array.isArray(searchProps?.commentedBy)) {
          result.commentedBy.value = confirmedUsers.filter((teammate) => searchProps?.commentedBy?.includes(teammate._id));
          if (isMultipleOperator) {
            result.commentedBy.excludedValues = confirmedUsers.filter((teammate) => searchProps?.params2?.commentedBy?.includes(teammate._id));
          }
        } else {
          result.commentedBy.value = [confirmedUsers.find((teammate) => teammate._id === searchProps?.commentedBy)];
          if (isMultipleOperator) {
            result.commentedBy.excludedValues = [confirmedUsers.find((teammate) => teammate._id === searchProps?.params2?.commentedBy)];
          }
        }
      }

      if (isMultipleOperator && searchProps?.params2?.commentedBy) {
        if (Array.isArray(searchProps?.params2?.commentedBy)) {
          result.commentedBy.excludedValues = confirmedUsers.filter((teammate) => searchProps?.params2?.commentedBy?.includes(teammate._id));
        } else {
          result.commentedBy.excludedValues = [confirmedUsers.find((teammate) => teammate._id === searchProps?.params2?.commentedBy)];
        }
      }
    }

    if (searchProps?.products) {
      if (Array.isArray(searchProps?.products)) {
        result.products.value = products.items.filter((product) => searchProps?.products?.includes(product._id));
      } else {
        result.products.value = [products.items.find((product) => product._id === searchProps?.products)];
      }
    }

    if (searchProps?.assignees || searchProps?.params2?.assignees) {
      const isMultipleOperator = MULTIPLE_OPERATORS.includes(result?.assignees?.operator);
      if (searchProps?.assignees) {
        if (Array.isArray(searchProps?.assignees)) {
          result.assignees.value = confirmedUsers.filter((teammate) => searchProps?.assignees?.includes(teammate._id));
        } else {
          result.assignees.value = [confirmedUsers.find((teammate) => teammate._id === searchProps?.assignees)];
        }
      }

      if (isMultipleOperator && searchProps?.params2?.assignees) {
        if (Array.isArray(searchProps?.params2?.assignees)) {
          result.assignees.excludedValues = confirmedUsers.filter((teammate) => searchProps?.params2?.assignees?.includes(teammate._id));
        } else {
          result.assignees.excludedValues = [confirmedUsers.find((teammate) => teammate._id === searchProps?.params2?.assignees)];
        }
      }
    }

    if (searchProps?.approvedBy || searchProps?.params2?.approvedBy) {
      const isMultipleOperator = MULTIPLE_OPERATORS.includes(result?.approvedBy?.operator);
      if (searchProps?.approvedBy) {
        if (Array.isArray(searchProps?.approvedBy)) {
          result.approvedBy.value = confirmedUsers.filter((teammate) => searchProps?.approvedBy?.includes(teammate._id));
        } else {
          result.approvedBy.value = [confirmedUsers.find((teammate) => teammate._id === searchProps?.approvedBy)];
        }
      }

      if (isMultipleOperator && searchProps?.params2?.approvedBy) {
        if (Array.isArray(searchProps?.params2?.approvedBy)) {
          result.approvedBy.excludedValues = confirmedUsers.filter((teammate) => searchProps?.params2?.approvedBy?.includes(teammate._id));
        } else {
          result.approvedBy.excludedValues = [confirmedUsers.find((teammate) => teammate._id === searchProps?.params2?.approvedBy)];
        }
      }
    }

    if (searchProps.createdBy) {
      if (Array.isArray(searchProps.createdBy)) {
        result.createdBy.value = confirmedUsers.filter((teammate) => searchProps.createdBy.includes(teammate._id));
      } else {
        result.createdBy.value = [confirmedUsers.find((teammate) => teammate._id === searchProps.createdBy)];
      }
    }

    if ((searchProps?.keywords && !SINGLE_OPERATORS.includes(result?.keywords?.operator)) || searchProps?.params2?.keywords) {
      const isMultipleOperator = MULTIPLE_OPERATORS.includes(result?.keywords?.operator);
      result.keywords.value = [];
      if (isMultipleOperator) result.keywords.excludedValues = [];
      const notFoundInTheStore = [];
      searchProps?.keywords?.forEach((id) => {
        const keyword = keywordsList.find((kw) => kw._id === id) || treeKeywords.find((kw) => kw._id === id);
        if (keyword) {
          const item = {
            _id: keyword._id,
            title: keyword.path.split('→').pop().toLowerCase(),
          };
          result.keywords.value.push(item);
        } else {
          notFoundInTheStore.push(id);
        }
      });

      if (isMultipleOperator) {
        searchProps?.params2?.keywords?.forEach((id) => {
          const keyword = keywordsList.find((kw) => kw._id === id) || treeKeywords.find((kw) => kw._id === id);
          if (keyword) {
            const item = {
              _id: keyword._id,
              title: keyword.path.split('→').pop().toLowerCase(),
            };
            result.keywords.excludedValues.push(item);
          } else {
            notFoundInTheStore.push(id);
          }
        });
      }

      if (notFoundInTheStore.length) {
        (async () => {
          try {
            const { payload } = await dispatch(getByIds(notFoundInTheStore));
            if (Array.isArray(payload)) {
              payload.forEach(({ _id, name }) => {
                if (searchProps.keywords.includes(_id)) {
                  result.keywords.value.push({ _id, title: name });
                }
                if (isMultipleOperator && searchProps?.params2?.keywords?.includes(_id)) {
                  result.keywords.excludedValues.push({ _id, title: name });
                }
              });
            }
          } catch (error) {
            Logger.error(new Error('Can not get keywords by IDS on toriut search filters'), { error, ids: notFoundInTheStore }, [
              'GetKeywordsByIdsFailedOnToriutSearchFilters',
              { errorMessage: (error && error.message) || 'NoMessage' },
            ]);
          }
        })();
      }
    }

    if ((searchProps?.productKeywords && !SINGLE_OPERATORS.includes(result?.productKeywords?.operator)) || searchProps?.params2?.productKeywords) {
      const isMultipleOperator = MULTIPLE_OPERATORS.includes(result?.productKeywords?.operator);
      result.productKeywords.value = [];
      if (isMultipleOperator) result.productKeywords.excludedValues = [];
      const notFoundInTheStore = [];
      searchProps?.productKeywords?.forEach((id) => {
        const keyword = keywordsList.find((kw) => kw._id === id) || treeKeywords.find((kw) => kw._id === id);
        if (keyword) {
          const item = {
            _id: keyword._id,
            title: keyword.path.split('→').pop().toLowerCase(),
          };
          result.productKeywords.value.push(item);
        } else {
          notFoundInTheStore.push(id);
        }
      });

      if (isMultipleOperator) {
        searchProps?.params2?.productKeywords?.forEach((id) => {
          const keyword = keywordsList.find((kw) => kw._id === id) || treeKeywords.find((kw) => kw._id === id);
          if (keyword) {
            const item = {
              _id: keyword._id,
              title: keyword.path.split('→').pop().toLowerCase(),
            };
            result.productKeywords.excludedValues.push(item);
          } else {
            notFoundInTheStore.push(id);
          }
        });
      }

      if (notFoundInTheStore.length) {
        (async () => {
          try {
            const { payload } = await dispatch(getByIds(notFoundInTheStore));
            if (Array.isArray(payload)) {
              payload.forEach(({ _id, name }) => {
                if (searchProps.productKeywords.includes(_id)) {
                  result.productKeywords.value.push({ _id, title: name });
                }
                if (isMultipleOperator && searchProps?.params2?.productKeywords?.includes(_id)) {
                  result.productKeywords.excludedValues.push({ _id, title: name });
                }
              });
            }
          } catch (error) {
            Logger.error(error, { source: 'SearchFiltersToriut.onRouteSearch.productKeywordsLoad' });
          }
        })();
      }
    }

    if (searchProps.inboxId) {
      result.inboxId.value = [inboxes.find((inbox) => inbox._id === activeInboxID)];
    }

    if (searchProps.collectionIds) {
      result.collectionIds.value = activeCollections;
    }

    if (searchProps?.watermarkId || searchProps?.params2?.watermarkId) {
      const isMultipleOperator = MULTIPLE_OPERATORS.includes(result?.watermarkId?.operator);
      if (searchProps?.watermarkId) {
        if (Array.isArray(searchProps.watermarkId)) {
          result.watermarkId.value = watermarks.filter((watermark) => searchProps.watermarkId.includes(watermark._id));
        } else {
          result.watermarkId.value = [watermarks.find((watermark) => watermark._id === searchProps.watermarkId)];
        }
      }

      if (isMultipleOperator && searchProps?.params2?.watermarkId) {
        if (Array.isArray(searchProps?.params2?.watermarkId)) {
          result.watermarkId.excludedValues = watermarks.filter((watermark) => searchProps.params2.watermarkId.includes(watermark._id));
        } else {
          result.watermarkId.excludedValues = [watermarks.find((watermark) => watermark._id === searchProps.params2.watermarkId)];
        }
      }
    }
    if (searchProps.mimeType && searchProps.mimeTypeOperator === 'oneOf') {
      const mimeTypes = Object.keys(MIME_TYPES).reduce((acc, key) => [...acc, ...MIME_TYPES[key]], []);
      const convertedMimeTypes = mimeTypes.map((item) => ({
        title: item, value: item, _id: item,
      }));
      if (Array.isArray(searchProps.mimeType)) {
        result.mimeType.value = searchProps.mimeType
          .map((mimeType) => convertedMimeTypes.find((converted) => converted._id === mimeType))
          .filter(Boolean); // Remove any undefined values
      } else {
        // Single value case
        const found = convertedMimeTypes.find((mimeType) => mimeType._id === searchProps.mimeType);
        result.mimeType.value = found ? [found] : [];
      }
    }

    if (searchProps?.faces || searchProps?.params2?.faces) {
      result.faces.value = [];
      const isMultipleOperator = MULTIPLE_OPERATORS.includes(result?.faces?.operator);
      if (isMultipleOperator) result.faces.excludedValues = [];

      searchProps?.faces?.forEach((id) => {
        const face = allFaces.find((face) => face._id === id);
        if (face) {
          const item = {
            _id: face._id,
            title: face.name.split('→').pop(),
          };
          result.faces.value.push(item);
        }
      });
      if (isMultipleOperator) {
        searchProps?.params2?.faces?.forEach((id) => {
          const face = allFaces.find((face) => face._id === id);
          if (face) {
            const item = {
              _id: face._id,
              title: face.name.split('→').pop().toLowerCase(),
            };
            result.faces.excludedValues.push(item);
          }
        });
      }
    }

    if (searchProps.searchSource) {
      result.searchSource = searchProps.searchSource;
    }

    if (searchProps.lightboardId) {
      result.lightboardId.value = [activeLightboard];
    }

    if (searchProps?.productLayer) {
      const layers = Array.isArray(searchProps.productLayer)
        ? searchProps.productLayer
        : [searchProps.productLayer];
      const rawOptions = availableSearchFilters.productLayer?.options || layers;
      const normalizedOptions = normalizeProductLayerOptions(rawOptions, teamSalesChannels);

      result.productLayer = {
        operator: 'oneOf',
        value: restoreProductLayerFilterValue(layers, normalizedOptions),
      };
    }

    if (searchProps.text) {
      result.text = searchProps.text;
    }
    Object.entries(result).forEach(([id, { operator, value }]) => {
      if (!operator) {
        if (id === 'collectionIds' || id === 'keywords' || id === 'faces' || id === 'productKeywords') {
          result[id].operator = 'oneOf';
        } else if (id === 'lightboardId' || id === 'inboxId' || id === 'trashed') {
          result[id].operator = 'equals';
        } else if (availableSearchFilters[id]) {
          if (availableSearchFilters[id].type === 'date' && SINGLE_OPERATORS.includes(value)) {
            result[id].operator = value;
            result[id].value = true;
          } else {
            result[id].operator = availableSearchFilters[id]?.defaultOperator;
          }
        }
      }
    });
    setSearchValue(result.text);
    setSelectedFilters(result);
    setConfirmedFilters(result);
    setActiveFilters(initFilters);
    setOrder(ensurePinnedSearchFiltersOrder(
      _unique(searchOrder),
      pinnedFilterIds,
    ));
  };

  const makeSearchRoute = useCallback((filters) => {
    const { archived } = getSearchProps();
    const rootCollectionId = rootCollection?._id;
    const multiValueKeys = new Set([
      'faces',
      'collectionIds',
      'lightboardId',
      'inboxId',
      'keywords',
      'productKeywords',
      'assignees',
      'commentedBy',
      'createdBy',
      'watermarkId',
      'mimeType',
      'approvedBy',
      'products',
    ]);

    const result = Object.entries(filters).reduce((acc, [id, { operator, value, excludedValues }]) => {
      if (id === 'productLayer') {
        acc[id] = serializeProductLayerForRoute(value);
        return acc;
      }

      const isMulti = multiValueKeys.has(id) || id.startsWith('meta.');

      if (isMulti) {
        acc[id] = Array.isArray(value) && value.length > 0
          ? value.map((item) => item?._id ?? item)
          : value;

        if (Array.isArray(excludedValues) && excludedValues.length > 0) {
          acc.params2 = {
            ...acc.params2,
            [id]: excludedValues.map((item) => item?._id ?? item),
          };
        }

        acc[`${id}Operator`] = operator;
      } else if (id !== 'params2') {
        acc[id] = value;
        acc[`${id}Operator`] = operator;
      }

      return acc;
    }, {});

    if (archived && searchSource.current) {
      result.archived = archived;
    }
    if (filters.text) result.text = filters.text;

    // eslint-disable-next-line no-param-reassign
    delete filters.searchSource;
    if (searchSource.current && !_isEmpty(filters)) result.searchSource = searchSource.current;

    if (location?.query?.archived && filters.collection?.value && filters.collection?.value.length > 0) {
      delete result.archived;
    }
    if (location?.query?.trashed && filters.collection?.value && (!filters.collection?.value.includes(rootCollectionId) || filters.collection?.value.length > 1)) {
      delete result.trashed;
    }
    if (openedTree && openedTree !== 'collections' && filters.collection?.value && filters.collection?.value.length > 0) {
      dispatch(changeTree('collections'));
    }
    if (Object.keys(filters).length === 0 && Object.keys(result).length === 0) {
      result.collectionIds = rootCollectionId;
    }

    return result;
  }, [dispatch, location?.query?.archived, location?.query?.trashed, openedTree, rootCollection?._id]);

  const setRoute = useCallback((filters) => {
    const isMobile = ua.browser.isNotDesktop() || ua.isMobileApp();
    internalSubmit.current = true;

    // for submit or reset, when route not changed
    setTimeout(() => {
      internalSubmit.current = false;
    }, 200);
    setSearchRoute(makeSearchRoute(filters), { isProducts: false });

    if (isMobile) {
      dispatch(setMobileAdditionalScreenPanel());
      dispatch(setMobileMainScreenPanel('catalog'));
    }
  }, [dispatch, makeSearchRoute]);

  const submit = useCallback((filters, isReset = false) => {
    searchSource.current = isReset ? null : 'searchbar';
    setRoute(filters);
  }, [setRoute]);

  const handleSelectFilter = useCallback((id, data) => {
    setSelectedFilters((prev) => ({ ...prev, [id]: data }));
  }, []);

  const isEntity = useCallback((id) => {
    if (id === 'collectionIds' || id === 'inboxId' || id === 'lightboardId') {
      return true;
    } return false;
  }, []);

  const getEntity = useCallback((filters) => Object.keys(filters).find((filter) => isEntity(filter)), [isEntity]);

  const handleConfirmFilter = useCallback((id, data, closeFilter = true) => {
    setConfirmedFilters((prev) => {
      const oldFilters = { ...prev };
      if (!oldFilters[id] && isEntity(id)) {
        const oldSearchEntity = getEntity(oldFilters);
        if (oldSearchEntity === 'collectionIds') {
          searchTagIds.current = null;
        } else if (oldSearchEntity === 'inboxId') {
          searchInboxId.current = null;
        } else if (oldSearchEntity === 'lightboardId') {
          searchLightboardId.current = null;
        }
        delete oldFilters[oldSearchEntity];
      }
      const nextFilters = {
        ...oldFilters,
        [id]: data,
      };
      if (searchValue) {
        nextFilters.text = searchValue;
      }

      Logger.log('User', 'FilterApplied', {
        filter_name: id,
        filter_operator: data.operator,
        filter_value: data.value,
        active_filters_list: prepareDataForAppliedFilterEvent(nextFilters).join(', '),
        active_filters_uniqueIDs: prepareDataForAppliedFilterEvent(nextFilters, true).join(', '),
        active_filters_count: prepareDataForAppliedFilterEvent(nextFilters).length,
        broad_search_value: searchValue,
      });

      submit(nextFilters);
      return nextFilters;
    });
    if (closeFilter) setOpen('');
  }, [getEntity, isEntity, searchValue, submit]);

  const handleResetAllFilters = useCallback((e, initiatedFrom) => {
    Logger.log('User', 'ResetAllFiltersClicked', initiatedFrom);
    setSearchValue('');
    if (searchInboxId.current) {
      setConfirmedFilters({ inboxId: confirmedFilters.inboxId });
      setSelectedFilters({ inboxId: selectedFilters.inboxId });
      submit({}, true);
      return;
    }
    if (searchLightboardId.current) {
      setConfirmedFilters({ lightboardId: confirmedFilters.lightboardId });
      setSelectedFilters({ lightboardId: selectedFilters.lightboardId });
      submit({}, true);
      return;
    }
    if (searchTagIds.current) {
      setConfirmedFilters({
        collectionIds: {
          operator: 'oneOf',
          value: [rootCollection],
        },
      });
      setSelectedFilters({
        collectionIds: {
          operator: 'oneOf',
          value: [rootCollection],
        },
      });
      submit({}, true);
      return;
    }
    setSelectedFilters({});
    setConfirmedFilters({});
    setOpen('');
    submit({}, true);
  }, [submit]);

  const handleResetFilter = useCallback((id) => {
    const resetCollectionFilter = (filters) => ({
      ...filters,
      collectionIds: {
        operator: 'oneOf',
        value: [rootCollection],
      },
    });

    setSelectedFilters((prev) => {
      const nextFilters = { ...prev };
      delete nextFilters[id];
      return (id === 'collectionIds' || id === 'inboxId' || id === 'lightboardId')
        ? resetCollectionFilter(nextFilters)
        : nextFilters;
    });

    setConfirmedFilters((prev) => {
      const nextFilters = { ...prev };
      Logger.log('User', 'ResetFilterClicked', {
        filter_name: id,
        filter_operator: nextFilters[id]?.operator || 'none',
        filter_value: nextFilters[id]?.value || 'none',
        remaining_filters_list: Object.keys(nextFilters).filter((key) => key !== id),
        remaining_filters_count: Object.keys(nextFilters).length - 1,
      });
      delete nextFilters[id];
      const updatedFilters = (id === 'collectionIds' || id === 'inboxId' || id === 'lightboardId')
        ? resetCollectionFilter(nextFilters)
        : nextFilters;
      submit(updatedFilters);
      return updatedFilters;
    });

    setOpen('');
  }, [submit, rootCollection]);

  const handleCustomizingMode = useCallback(() => {
    Logger.log('User', 'ManageFiltersClicked');
    setCustomizingMode((prev) => !prev);
    setPrevFilters(activeFilters);
    setPrevOrder(order);
  }, [activeFilters, order]);

  const handleSavedSearchSave = useCallback(() => {
    showSaveSearchDialog({
      user,
      searchData: makeSearchRoute(confirmedFilters),
      entity: 'assets',
      searchValue,
      confirmedFilters,
    });
  }, [confirmedFilters, makeSearchRoute, searchValue, user]);

  const handleCancelCustomizingMode = useCallback(() => {
    Logger.log('User', 'CancelFilterConfigurationClicked');
    setCustomizingMode(false);
    setActiveFilters(prevFilters);
    setOrder(prevOrder);
  }, [prevFilters, prevOrder]);

  const handleSaveFilters = useCallback(() => {
    Logger.log('User', 'SaveFilterConfigurationClicked');
    const isFiltersOrderChanged = !_isEqual(order, prevOrder);
    if (isFiltersOrderChanged) {
      Logger.log('User', 'ChangeFilterDisplayOrder', {
        changed_filter_position: order,
      });
      recalculateItems();
    }
    setCustomizingMode(false);
    setPrevFilters(activeFilters);
    setPrevOrder(order);
    const assetsOrder = ensurePinnedSearchFiltersOrder(order, pinnedFilterIds);
    dispatch(updateUser({
      settings: {
        favoriteSearchFilters: {
          assets: assetsOrder,
          products: [...(user.settings?.favoriteSearchFilters?.products || [])],
        },
      },
    }, false));
  }, [activeFilters, dispatch, order, user?.settings?.favoriteSearchFilters?.products]);

  const checkActive = useCallback((id) => Boolean(confirmedFilters[id]), [confirmedFilters]);

  const checkOpen = useCallback((id) => id === open, [open]);

  const handleOpen = useCallback(
    (id) => {
      if (customizingMode) return;
      setOpen(id);
    }, [customizingMode],
  );

  const handleClose = useCallback(
    (e) => {
      e.stopPropagation();
      if (customizingMode) return;
      setOpen('');
    }, [customizingMode],
  );

  const handleRemoveFilter = useCallback((id) => {
    if (isPinnedSearchFilter(id, pinnedFilterIds)) return;
    Logger.log('User', 'RemoveFilterClicked', {
      removed_filter: id,
    });
    setOrder(order.filter((or) => or !== id));
  }, [order]);

  const handleAddFilters = useCallback((filters) => {
    const filtersIds = filters.map((filter) => filter.id);
    Logger.log('User', 'AddFiltersClicked', {
      selected_filter_name: filtersIds,
    });
    setOrder([...order, ...filtersIds]);
  }, [order]);

  const handleShowAddFiltersDialog = useCallback(() => {
    showAddNewFiltersDialog({ filters: inactiveFilters, onOk: handleAddFilters, isToriutMedia: true });
  }, [handleAddFilters, inactiveFilters]);

  const preventDefault = (event) => {
    event.preventDefault();
  };

  const dragstart = (event) => {
    document.body.addEventListener('dragover', preventDefault);
    document.body.addEventListener('drop', preventDefault);

    event.stopPropagation();

    const el = (originalEl.current = event.currentTarget);
    draggableElement.current = event.currentTarget;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', 'Data to Drag');
    tmpItem.current = templateTmpItem.current;

    tmpPosition.current = itemToMove.current = itemToMoveAfter.current = event.currentTarget.dataset.id;

    setTimeout(() => {
      el.parentNode.insertBefore(tmpItem.current, el);
      el.style.display = 'none';
    }, 0);
  };

  const dragenter = (event) => {
    if (!draggableElement.current) return;
    event.stopPropagation();
    const { currentTarget } = event;
    const { id } = currentTarget.dataset;
    const el = currentTarget;
    el.after(tmpItem.current);

    tmpPosition.current = itemToMoveAfter.current = id;
  };

  const dragend = () => {
    document.body.removeEventListener('dragover', preventDefault);
    document.body.removeEventListener('drop', preventDefault);
    tmpItem.current.remove();
    draggableElement.current = null;
    originalEl.current.style.display = 'inline-block';
    if (itemToMoveAfter.current === 'first') {
      itemToMoveAfter.current = '';
    }

    const fieldToMove = activeFilters.find((item) => item.id === itemToMove.current);
    const fieldToMoveAfter = activeFilters.find(
      (item) => item.id === itemToMoveAfter.current,
    );

    if (!fieldToMove || fieldToMove === fieldToMoveAfter) return;
    if (isPinnedSearchFilter(fieldToMove.id, pinnedFilterIds)) return;

    const fieldsToMove = [fieldToMove.id];
    const currentFieldToMoveAfter = itemToMoveAfter.current;

    const newItems = [...order];
    const indexToRemove = newItems.findIndex((item) => item === fieldsToMove[0]);
    const itemsToMove = newItems.splice(indexToRemove, fieldsToMove.length);
    const indexToPut = currentFieldToMoveAfter ? newItems.findIndex((item) => item === currentFieldToMoveAfter) : -1;
    newItems.splice(indexToPut + 1, 0, ...itemsToMove);
    setOrder(ensurePinnedSearchFiltersOrder(newItems, pinnedFilterIds));
  };

  const getCurrentSortType = () => {
    const { collectionIds, lightboardId, inboxId } = location.query;
    let sortTypeNew = sortType;

    if (collectionIds && collectionsSortType.length) {
      if (collectionsSortType.length === 1) {
        sortTypeNew = collectionsSortType[0];
      } else {
        const equalSortings = checkForCollectionsSortingsEquality(collectionsSortType);
        if (equalSortings) {
          sortTypeNew = collectionsSortType[0];
        } else {
          sortTypeNew = defaultSort;
        }
      }
    } else if (lightboardId && lightboardsSortType) {
      sortTypeNew = lightboardsSortType;
    } else if (inboxId) {
      if (activeInboxSortType) {
        sortTypeNew = activeInboxSortType;
      }
    }

    return sortTypeNew;
  };

  const changeSortType = () => {
    const sortTypeNew = getCurrentSortType();
    if (sortTypeNew !== sortType) {
      setSortType(sortTypeNew);
    }
  };

  useEffect(() => {
    if ((isRouteSearch() && prevLocation !== location) || (
      prevCollectionsSortType !== collectionsSortType[0]
      || prevCollectionsSortTypeLength !== collectionsSortType.length
      || prevLightboardsSortType !== lightboardsSortType
      || prevInboxesSortType !== activeInboxSortType
    )) {
      changeSortType();
    }
  });

  const handleChangeSort = useCallback(async (name, order) => {
    if (picsioConfig.isMainApp) {
      const { collectionIds, lightboardId, inboxId } = location.query;
      if (collectionIds) {
        UtilsCollections.setSortType({
          type: name,
          order,
        });
      }

      if (inboxId) {
        await dispatch(setInboxSortType({ _id: inboxId, sortType: { type: name, order } }));
        reloadRoute();
      }

      if (lightboardId) {
        dispatch(updateSortType(lightboardId, {
          type: name,
          order,
        }));
      }
    } else {
      const newSortType = { type: name, order };
      setSortType(newSortType);
      window.websiteConfig.sortType = newSortType;
      dispatch(getAssets(true));
    }
  }, [dispatch, location.query]);

  useEffect(() => {
    setOrder((prev) => ensurePinnedSearchFiltersOrder(
      searchFiltersOrder.length ? searchFiltersOrder : prev,
      pinnedFilterIds,
    ));
  }, [searchFiltersOrder, pinnedFilterIds]);

  useEffect(() => {
    onRouteSearch();
  }, [activeCollections, allFaces, location.key, activeLightboard, activeInboxID]);

  const getSearchPlaceholder = useMemo(() => {
    let placeholder = 'Search all collections';

    if (picsioConfig.isPim) {
      placeholder = 'Type to search';
      return placeholder;
    }
    const {
      lightboardId, inboxId, archived, collectionIds, trashed,
    } = getSearchProps();
    const rootCollectionId = rootCollection?._id;

    // archive
    if (collectionIds && archived) {
      if (activeCollections?.name && activeCollections?.name !== rootCollection.name) {
        placeholder = `Search in '${utils.decodeSlash(activeCollections.name)}'`;
      }
    } else if (trashed && activeCollections.every(({ _id }) => _id === rootCollectionId)) {
      placeholder = 'Search in trashed assets';
    } else if (collectionIds) {
      if (activeCollections.length === 1 && activeCollections[0]?.name && activeCollections[0]?.name !== rootCollection.name) {
        placeholder = `Search in '${utils.decodeSlash(activeCollections[0].name)}'`;
      } else if ((activeCollections.length === 1 && activeCollections.find(({ _id }) => _id !== rootCollectionId))) {
        placeholder = `Search in '${utils.decodeSlash(activeCollections[0].name)}'`;
      } else if (activeCollections.length > 1) {
        placeholder = 'Search in multiple collections';
      }
    } else if (lightboardId) {
      if (activeLightboard?.path) {
        const name = activeLightboard.path.split('→').pop();
        placeholder = `Search in '${decodeURIComponent(name)}'`;
      }
    } else if (inboxId) {
      const inbox = inboxes.find((item) => item._id === inboxId) || {};
      if (inbox?.name) {
        const { name } = inbox;
        placeholder = `Search in '${decodeURIComponent(name)}'`;
      }
    }
    return placeholder;
  }, [activeCollections, activeLightboard, inboxes, rootCollection?._id, rootCollection?.name, location.key]);

  const handleSearchValueChange = useCallback((searchText) => {
    setSearchValue(searchText);
  }, []);

  const transformSuggestTextToFilters = useCallback((suggestion) => ({
    ...confirmedFilters,
    ...suggestion.filter,
  }), [confirmedFilters]);

  const submitSearchValue = async (text, suggestion) => {
    if (suggestion?.filter) {
      const data = transformSuggestTextToFilters(suggestion);
      setSearchRoute(makeSearchRoute({ ...data }), { isProducts: false });
    } else {
      setSearchRoute(makeSearchRoute({ ...confirmedFilters, text }), { isProducts: false });
    }
    Logger.log('User', 'Search', {
      text: searchValue,
      ...location.query,
    });

    const booleanSearchOperatorsInUse = new Set();
    booleanSearchOperators.forEach((operator) => {
      if (searchValue.includes(operator)) {
        booleanSearchOperatorsInUse.add(operator);
      }
    });
    if (booleanSearchOperatorsInUse.size) {
      Logger.log('User', 'SearchWithBooleanOperators', {
        operators: Array.from(booleanSearchOperatorsInUse),
      });
    }
  };

  const resetSearchValue = useCallback(() => {
    setSearchValue('');

    setSearchRoute(makeSearchRoute({ ...confirmedFilters, text: '' }), { isProducts: false });
  }, [confirmedFilters, makeSearchRoute]);

  useEffect(() => {
    let itemsRef = [];
    if (itemsMediaRef.current.length > 0 && itemsProductsRef.current.length > 0) {
      itemsRef = [...itemsMediaRef.current, ...itemsProductsRef.current];
    } else if (itemsMediaRef.current.length > 0) {
      itemsRef = itemsMediaRef.current;
    } else if (itemsProductsRef.current.length > 0) {
      itemsRef = itemsProductsRef.current;
    }
    const widths = itemsRef?.reduce((acc, ref) => {
      if (ref?.current) {
        const width = ref.current.clientWidth;
        acc[ref.current.dataset.id] = { width };
      }
      return acc;
    }, itemWidths || {});
    setItemWidths(widths);
  }, [itemWidths, renderItems]);

  const recalculateItems = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    // here 20px for the 10px padding for left and right sides
    const containerWidth = container.clientWidth - (controlsRef?.current?.clientWidth || 144) - 25;
    let totalWidth = 0;
    let visibleItemsCount = 0;
    // Measure the width of the plus indicator (if it is rendered)
    const plusWidth = hiddenItemsCounterRef.current ? hiddenItemsCounterRef.current.clientWidth : 70;
    for (let i = 0; i < renderItems.length; i++) {
      const { id } = renderItems[i];
      const { width: itemWidth } = itemWidths[id] || { width: 0 };
      // If there are more items after this one, account for the plus indicator's width
      const additionalSpace = (i < renderItems.length - 1) ? plusWidth : 0;
      if (totalWidth + itemWidth + additionalSpace < containerWidth) {
        totalWidth += itemWidth;
        visibleItemsCount++;
      } else { break; }
    }
    if (visibleItemsCount !== renderItems.length || visibleItemsCount !== items.length) {
      const newItems = [...renderItemsMedia, ...renderItemsProducts];
      newItems.length = visibleItemsCount;
      setItems(newItems);
      setHiddenItemsCount(renderItems.length - visibleItemsCount);
    }
  }, [itemWidths, items.length, renderItems, renderItemsMedia, renderItemsProducts]);

  const collapse = useCallback(() => {
    setIsCollapsed(true);
    setIsHovering(false);
    recalculateItems();
    utils.LocalStorage.set('picsio.searchFiltersMinimized', true);
    Logger.log('User', 'MinimizeFiltersPanel', { screenSize: { width: window.screen.width, height: window.screen.height } });
  }, [recalculateItems]);

  const expand = useCallback((e, initiatedFrom) => {
    setIsCollapsed(false);
    setIsHovering(false);
    utils.LocalStorage.set('picsio.searchFiltersMinimized', false);
    Logger.log('User', 'ExpandFiltersPanel', { initiatedFrom, screenSize: { width: window.screen.width, height: window.screen.height } });
  }, []);

  useEffect(() => {
    recalculateItems();
    const container = containerRef.current;
    const resizeObserver = new ResizeObserver(() => recalculateItems());
    if (container) {
      resizeObserver.observe(container);
    }
    window.addEventListener('resize', recalculateItems);

    return () => {
      if (container) {
        resizeObserver.unobserve(container);
      }
      window.removeEventListener('resize', recalculateItems);
    };
  }, [renderItems]);

  useEffect(() => {
    // sometimes styles for the filters are not applied, which causes wrong calculations
    const timer = setTimeout(() => {
      recalculateItems();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleMouseEnter = useCallback(() => {
    clearTimeout(timeout.current);
    setIsHovering(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    timeout.current = setTimeout(() => setIsHovering(false), 500);
  }, []);

  const hiddenItemsHaveActiveFilter = useMemo(() => {
    const activeFiltersIds = Object.keys(confirmedFilters).filter((id) => id !== 'text');

    return activeFiltersIds.every((id) => {
      if (id === 'collectionIds' && confirmedFilters[id]?.value?.length === 1 && confirmedFilters[id].value[0]._id === rootCollection?._id) {
        return true;
      } if (!items.find((item) => item.id === id)) {
        return false;
      } return true;
    });
  }, [confirmedFilters, items, rootCollection?._id]);

  const searchInputNode = (
    <div id="searchInput">
      <div className="searchHeader">
        <span className="btnSearch">
          <Icon size="md" color="inherit">
            <SearchIcon />
          </Icon>
        </span>
        <Autocomplete
          placeholder={getSearchPlaceholder}
          searchValue={searchValue}
          handleChange={handleSearchValueChange}
          handleSubmit={submitSearchValue}
          fromSearchFilters
        />
        <Counter amount={assets.total} className="filters" />
        <If condition={searchValue.length > 0}>
          <IconButton
            size="xl"
            className="btnReset"
            componentProps={{
              'data-testid': 'btnResetSearchValue',
            }}
            onKeyPress={resetSearchValue}
            onClick={resetSearchValue}
          >
            <Close />
          </IconButton>
        </If>
        <If condition={searchValue.length > 0}>
          <div
            className="picsioDefBtn btnSearch btnCallToAction btnSubmitText"
            onClick={() => {
              Logger.log('User', 'AdvancedSearchPanelConfirm', { ...location.query });
              submit({ ...confirmedFilters, text: searchValue });
            }}
          >
            {l10n.SEARCH.text.Search}
          </div>
        </If>
      </div>
      <If condition={picsioConfig.isPim}>
        <TrialDemoControl />
      </If>
    </div>
  );

  return (
    <>
      <div className={cn('searchFilters__wrapper', { onlyTextSearch })} style={{ justifyContent: picsioConfig.isProofing ? 'unset' : 'space-between' }}>
        <div className="wrapperSearch searchFilters">
          <Choose>
            <When condition={searchInputTarget}>
              {ReactDOM.createPortal(searchInputNode, searchInputTarget)}
            </When>
            <Otherwise>
              {searchInputNode}
            </Otherwise>
          </Choose>
        </div>
        <MediaSearchFilters
          onlyTextSearch={onlyTextSearch}
          customizingMode={customizingMode}
          containerRef={containerRef}
          handleMouseEnter={handleMouseEnter}
          handleMouseLeave={handleMouseLeave}
          isHovering={isHovering}
          hiddenItemsCount={hiddenItemsCount}
          isCollapsed={isCollapsed}
          expand={expand}
          collapse={collapse}
          controlsRef={controlsRef}
          resetIsHidden={resetIsHidden}
          hiddenItemsHaveActiveFilter={hiddenItemsHaveActiveFilter}
          items={items}
          renderItems={renderItemsMedia}
          handleResetAllFilters={handleResetAllFilters}
          handleCustomizingMode={handleCustomizingMode}
          handleSavedSearchSave={handleSavedSearchSave}
          handleSelectFilter={handleSelectFilter}
          handleConfirmFilter={handleConfirmFilter}
          handleResetFilter={handleResetFilter}
          handleOpen={handleOpen}
          handleClose={handleClose}
          handleRemoveFilter={handleRemoveFilter}
          dragstart={dragstart}
          dragenter={dragenter}
          dragend={dragend}
          FILTERS={FILTERS}
          rootCollection={rootCollection}
          itemsMediaRef={itemsMediaRef}
          itemsProductsRef={itemsProductsRef}
          hiddenItemsCounterRef={hiddenItemsCounterRef}
          sortType={sortType}
          handleChangeSort={handleChangeSort}
          handleShowAddFiltersDialog={handleShowAddFiltersDialog}
          isRouteSearch={isRouteSearch}
          location={location}
          selectedFilters={selectedFilters}
          confirmedFilters={confirmedFilters}
          checkActive={checkActive}
          checkOpen={checkOpen}
          renderItemsProducts={renderItemsProducts}
          pinnedFilterIds={pinnedFilterIds}
        />

        <If condition={customizingMode && !location.pathname.startsWith('/product/')}>
          <div className="searchFilters__controls" style={{ padding: '10px 12px' }}>
            <IconButton
              size="md"
              className="searchFilters__iconBtn searchFilters__customizeIconBtn"
              onClick={handleCancelCustomizingMode}
            >
              <CloseIcon />
            </IconButton>
            <Tooltip
              content={l10n.SEARCH.saveFiltersTooltip}
              placement="top"
            >
              <IconButton
                size="md"
                className="searchFilters__iconBtn searchFilters__customizeIconBtn"
                onClick={handleSaveFilters}
              >
                <CheckIcon />
              </IconButton>
            </Tooltip>
          </div>
        </If>
      </div>
    </>
  );
};

export default SearchFiltersToriut;
